/** TOTP RFC 6238 — HMAC-SHA1, 30s, 6 cifre (Google Authenticator / Authy). */

const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'

export function generateTotpSecret(bytes = 20): string {
  const buf = crypto.getRandomValues(new Uint8Array(bytes))
  return encodeBase32(buf)
}

export function encodeBase32(data: Uint8Array): string {
  let bits = 0
  let value = 0
  let out = ''
  for (const byte of data) {
    value = (value << 8) | byte
    bits += 8
    while (bits >= 5) {
      out += BASE32_ALPHABET[(value >>> (bits - 5)) & 31]
      bits -= 5
    }
  }
  if (bits > 0) out += BASE32_ALPHABET[(value << (5 - bits)) & 31]
  return out
}

export function decodeBase32(secret: string): Uint8Array {
  const cleaned = secret.replace(/[\s=-]/g, '').toUpperCase()
  let bits = 0
  let value = 0
  const out: number[] = []
  for (const ch of cleaned) {
    const idx = BASE32_ALPHABET.indexOf(ch)
    if (idx < 0) continue
    value = (value << 5) | idx
    bits += 5
    if (bits >= 8) {
      out.push((value >>> (bits - 8)) & 255)
      bits -= 8
    }
  }
  return new Uint8Array(out)
}

function counterToBytes(counter: number): Uint8Array {
  const buf = new ArrayBuffer(8)
  const view = new DataView(buf)
  // high 32 bits = 0 for practical TOTP lifetimes
  view.setUint32(0, 0, false)
  view.setUint32(4, counter >>> 0, false)
  return new Uint8Array(buf)
}

async function hmacSha1(key: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key as BufferSource,
    { name: 'HMAC', hash: { name: 'SHA-1' } },
    false,
    ['sign'],
  )
  const sig = await crypto.subtle.sign({ name: 'HMAC' }, cryptoKey, data as BufferSource)
  return new Uint8Array(sig)
}

export async function generateTotpCode(
  secretBase32: string,
  timeMs = Date.now(),
  stepSec = 30,
  digits = 6,
): Promise<string> {
  const key = decodeBase32(secretBase32)
  if (key.length < 10) throw new Error('totp_secret_invalid')
  const counter = Math.floor(timeMs / 1000 / stepSec)
  const hmac = await hmacSha1(key, counterToBytes(counter))
  const offset = hmac[hmac.length - 1]! & 0x0f
  const bin =
    ((hmac[offset]! & 0x7f) << 24) |
    ((hmac[offset + 1]! & 0xff) << 16) |
    ((hmac[offset + 2]! & 0xff) << 8) |
    (hmac[offset + 3]! & 0xff)
  const mod = 10 ** digits
  return String(bin % mod).padStart(digits, '0')
}

export async function verifyTotpCode(
  secretBase32: string,
  code: string,
  windowSteps = 1,
): Promise<boolean> {
  const trimmed = code.replace(/\s/g, '')
  if (!/^\d{6}$/.test(trimmed)) return false
  const now = Date.now()
  for (let w = -windowSteps; w <= windowSteps; w++) {
    const candidate = await generateTotpCode(secretBase32, now + w * 30_000)
    if (candidate === trimmed) return true
  }
  return false
}

export function buildOtpAuthUrl(
  secretBase32: string,
  accountName: string,
  issuer = 'ZION',
): string {
  const label = encodeURIComponent(`${issuer}:${accountName}`)
  const params = new URLSearchParams({
    secret: secretBase32.replace(/\s/g, ''),
    issuer,
    algorithm: 'SHA1',
    digits: '6',
    period: '30',
  })
  return `otpauth://totp/${label}?${params.toString()}`
}
